// FieldTypes.js — schema field type ids and legacy type detection

/**
 * Normalizes a schema field type id (lowercase).
 * Maps legacy `nodelist` → canonical `relation`.
 *
 * @param {*} value - Raw field type.
 * @param {string} [fallback=''] - Fallback when empty.
 * @returns {string} Field type id.
 */
export function normalizeSchemaFieldType(value, fallback = '') {
  const normalized = String(value ?? fallback).trim().toLowerCase();
  if (!normalized) return String(fallback || '').trim().toLowerCase();
  // Legacy alias: Relation fields were stored as `nodelist`.
  if (normalized === 'nodelist') return 'relation';
  return normalized;
}

/**
 * Legacy money amounts were stored as type `currency` with a field-level `currencyCode`.
 * New `currency` fields store an ISO code as the value and do not set `currencyCode` on the def.
 *
 * @param {object|null|undefined} field - Schema field.
 * @returns {boolean}
 */
export function isLegacyMoneyCurrencyField(field) {
  if (!field || typeof field !== 'object') return false;
  const type = normalizeSchemaFieldType(field.type);
  if (type !== 'currency') return false;
  return Object.prototype.hasOwnProperty.call(field, 'currencyCode');
}

/**
 * Canonicalizes a schema field (rewrites legacy money `currency` → `money`,
 * and legacy `nodelist` → `relation`).
 *
 * @param {object|null|undefined} field - Schema field.
 * @returns {object|null|undefined} Field with canonical type when applicable.
 */
export function canonicalizeSchemaField(field) {
  if (!field || typeof field !== 'object') return field;
  let next = field;
  if (isLegacyMoneyCurrencyField(field)) {
    next = { ...field, type: 'money' };
  }
  const rawType = String(next.type ?? '').trim().toLowerCase();
  const canonicalType = normalizeSchemaFieldType(next.type);
  if (canonicalType && canonicalType !== rawType) {
    next = { ...next, type: canonicalType };
  }
  return next;
}

/**
 * True when the type/field is a monetary amount (`money`, or legacy currency+currencyCode).
 *
 * @param {*} value - Raw field type, or a field object.
 * @returns {boolean}
 */
export function isMoneyFieldType(value) {
  if (value && typeof value === 'object') {
    const type = normalizeSchemaFieldType(value.type);
    if (type === 'money') return true;
    return isLegacyMoneyCurrencyField(value);
  }
  return normalizeSchemaFieldType(value) === 'money';
}

/**
 * True when the type is an ISO currency-code field (not a money amount).
 *
 * @param {*} value - Raw field type, or a field object.
 * @returns {boolean}
 */
export function isCurrencyCodeFieldType(value) {
  if (value && typeof value === 'object') {
    const type = normalizeSchemaFieldType(value.type);
    return type === 'currency' && !isLegacyMoneyCurrencyField(value);
  }
  return normalizeSchemaFieldType(value) === 'currency';
}

/**
 * True when the type/field is a Relation (`relation`, or legacy `nodelist`).
 *
 * @param {*} value - Raw field type, or a field object.
 * @returns {boolean}
 */
export function isRelationFieldType(value) {
  if (value && typeof value === 'object') {
    return normalizeSchemaFieldType(value.type) === 'relation';
  }
  return normalizeSchemaFieldType(value) === 'relation';
}

/**
 * Single-select option fields that share enum UI, pills, filters, and board grouping.
 * Includes `enum` and `pipeline` (not `multiselect`).
 *
 * @param {*} value - Raw field type, or a field object.
 * @returns {boolean}
 */
export function isEnumCompatibleFieldType(value) {
  const type = value && typeof value === 'object'
    ? normalizeSchemaFieldType(value.type)
    : normalizeSchemaFieldType(value);
  return type === 'enum' || type === 'pipeline';
}

/**
 * Fields that store a list of selectable options (`enum`, `pipeline`, `multiselect`).
 *
 * @param {*} value - Raw field type, or a field object.
 * @returns {boolean}
 */
export function isOptionsListFieldType(value) {
  const type = value && typeof value === 'object'
    ? normalizeSchemaFieldType(value.type)
    : normalizeSchemaFieldType(value);
  return type === 'enum' || type === 'pipeline' || type === 'multiselect';
}
